import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import type { Media, Reaction } from '../../api/types'
import { ChevronLeftIcon, ChevronRightIcon, ReplyIcon, SichernIcon, XIcon } from '../../components/Icons'
import { sichereFoto } from '../../lib/sichern'
import {
  abstand,
  begrenze,
  fuellFaktor,
  zoomeAuf,
  HAND_DECKEL,
  type Masse,
  type Punkt,
} from '../../lib/bildzoom'
import { Reactions } from './Reactions'

/**
 * Die Fotos eines Beitrags.
 *
 * Ein einzelnes Bild steht für sich und behält sein Seitenverhältnis -- ein
 * Hochformat vom Telefon in ein Quadrat zu schneiden nimmt gerade das weg,
 * worum es ging. Ab zwei Bildern wird daraus ein Raster aus Quadraten, denn
 * nebeneinander gestellte Bilder unterschiedlicher Form ergeben eine unruhige
 * Treppe.
 *
 * Ab dem fünften Bild zeigt die letzte Kachel, wie viele noch folgen. Die
 * Großansicht hat sie ohnehin alle.
 */
export function Gallery({
  media,
  reactions,
  canReact,
  onReact,
  onReply,
}: {
  media: Media[]
  reactions: Reaction[]
  canReact: boolean
  onReact: (emoji: string, mediaId: number) => void
  /**
   * Auf genau dieses Foto antworten.
   *
   * Der Knopf sitzt nur in der Großansicht und nicht auf der Kachel: die
   * öffnet die Großansicht, und ein zweites Ziel auf 56 Pixeln ist Gefummel.
   * Erst groß sieht man ohnehin, welches Bild man meint.
   */
  onReply?: (mediaId: number) => void
}) {
  const [open, setOpen] = useState<number | null>(null)

  if (media.length === 0) return null

  const fuer = (id: number) => reactions.filter((reaction) => reaction.media_id === id)

  const shown = media.length > 4 ? media.slice(0, 4) : media
  const hidden = media.length - shown.length

  return (
    <>
      <div
        className={media.length === 1 ? 'gallery gallery--single' : 'gallery'}
        style={media.length > 1 ? { gridTemplateColumns: `repeat(${media.length === 3 ? 3 : 2}, 1fr)` } : undefined}
      >
        {shown.map((item, index) => (
          <button
            key={item.id}
            type="button"
            className="gallery__tile"
            onClick={() => setOpen(index)}
            aria-label={`Foto ${index + 1} von ${media.length} vergrößern`}
          >
            <img
              /* **Die Kachel nimmt das Vorschaubild** (#57). Sie ist rund 120
                 Punkte breit und lud bis hierher das volle Foto -- bei zwanzig
                 Fotos zwanzig Megabyte, und genau die wirft iOS als Erstes aus
                 dem Zwischenspeicher. Fehlt es (alles von vor #57), steht das
                 Original darin wie bisher. */
              src={item.thumb_url ?? item.url}
              alt=""
              loading="lazy"
              // Die Maße stehen in der Antwort. Der Platz ist damit reserviert,
              // bevor das Bild da ist, und der Verlauf springt beim Laden nicht.
              width={item.width}
              height={item.height}
            />
            {hidden > 0 && index === shown.length - 1 && (
              <span className="gallery__more">+{hidden}</span>
            )}

            {/* Was an diesem Foto hängt, steht auf dem Foto -- sonst wüsste
                bei vier Bildern niemand, welches gemeint ist. */}
            {fuer(item.id).length > 0 && (
              <span className="gallery__reactions">
                {fuer(item.id).map((reaction) => (
                  <span key={reaction.emoji}>
                    {reaction.emoji}
                    {reaction.count > 1 && reaction.count}
                  </span>
                ))}
              </span>
            )}
          </button>
        ))}
      </div>

      {open !== null && (
        <Lightbox
          media={media}
          start={open}
          reactionsFor={fuer}
          canReact={canReact}
          onReact={onReact}
          onReply={onReply}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  )
}


/**
 * Großansicht.
 *
 * Schließt mit Escape, Klick auf den Hintergrund oder Wischen. Geblättert wird
 * mit den Pfeiltasten -- am Telefon mit dem Daumen.
 *
 * **Sie wird an den Seitenkörper gehängt, nicht dorthin, wo sie steht.** Auf
 * der Kartenseite lag sie sonst unter der Karte (Leaflet baut seine Ebenen bis
 * Rang 1000) und im Formular wurde sie vom scrollenden Blatt abgeschnitten --
 * am iPad und am iPhone genau so gemeldet. Ein Bild in Großansicht gehört
 * keinem Kasten, es gehört dem Bildschirm.
 *
 * **Und seit #60 gehört ihm auch die Fläche** — der 16-Punkte-Rahmen ist weg,
 * Kopf und Leiste liegen über dem Bild und treten bei einem Tipp beiseite.
 * Aus dem Betrieb: „Fotos im Querformat in der Galerieansicht sollen auch auf
 * den ganzen Bildschirm vergrößert werden." Gemessen war ein Querformat 4:3
 * am iPhone 358 × 268 Punkte groß — ein Viertel der Fläche, der Rest dunkel.
 * Am iPad fiel es nicht auf, weil dort das Fenster fast dasselbe Verhältnis
 * hat wie das Bild; es war also nie ein Fehler eines Geräts, sondern eine
 * Regel, die nur bei passendem Verhältnis aufgeht.
 *
 * **Ein Rahmen allein reicht dafür aber nicht.** Ein liegendes Foto auf einem
 * stehenden Schirm füllt ihn nie, solange es ganz zu sehen sein soll. Deshalb
 * der Zoom: Doppeltipp, zwei Finger, schieben. Nichts wird beschnitten — es
 * wird herangeholt, und der Ausschnitt gehört dem, der schaut.
 */
function Lightbox({
  media,
  start,
  reactionsFor,
  canReact,
  onReact,
  onReply,
  onClose,
}: {
  media: Media[]
  start: number
  reactionsFor: (mediaId: number) => Reaction[]
  canReact: boolean
  onReact: (emoji: string, mediaId: number) => void
  onReply?: (mediaId: number) => void
  onClose: () => void
}) {
  const [index, setIndex] = useState(start)
  /**
   * Wo der Finger aufsetzte — als Zettel, nicht als Zustand.
   *
   * **Hier stand `useState`, und das ist die falsche Schublade.** Der Wert
   * wird nirgends gezeichnet; gebraucht wird er nur im nächsten Ereignis. Als
   * Zustand hängt er daran, dass zwischen Aufsetzen und Loslassen wirklich
   * neu gezeichnet wurde — bei einem Finger stimmt das, bei einem schnellen
   * Wisch ist es Glück. `HighlightBlatt` macht es mit `griff` seit jeher so,
   * und genau dieser Unterschied fiel beim Nachmessen auf: dessen Blättern
   * löste aus, das der Großansicht nicht.
   *
   * Seit #60 trägt derselbe Zettel mehr: die Lage des Ausschnitts beim
   * Aufsetzen, damit das Schieben nicht auf ein Neuzeichnen wartet.
   */
  const griff = useRef<{ x: number; y: number; versatz: Punkt; zeit: number } | null>(null)

  /**
   * Was beim Sichern herausgekommen ist — als Satz, nicht als `title`.
   *
   * **Keine stille Absage:** wer den Knopf antippt und nichts sieht, hält die
   * Anwendung für kaputt. Auf dem Telefon spricht meist das Teilen-Blatt für
   * sich; dort bleibt es still. Gesagt wird, was der Mensch sonst nicht sieht:
   * dass es lief, dass es geladen wurde, oder dass es schiefging.
   */
  const [meldung, setMeldung] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  // --- Zoom und Ausschnitt (#60) --------------------------------------------

  /** 1 heißt eingepasst — das ganze Bild ist zu sehen. */
  const [zoom, setZoom] = useState(1)
  const [versatz, setVersatz] = useState<Punkt>({ x: 0, y: 0 })
  /**
   * Ob die Bewegung geglättet wird.
   *
   * **Beim Doppeltipp ja, beim Ziehen nein.** Eine Überblendung unter dem
   * Finger hinkt hinterher und fühlt sich an, als klemme etwas.
   */
  const [sanft, setSanft] = useState(true)
  /** Knöpfe und Leiste ausgeblendet — ein Tipp aufs Bild schaltet um. */
  const [nackt, setNackt] = useState(false)

  /**
   * Die wahre Größe des Bildes.
   *
   * Sie steht in der Antwort des Servers (`width`/`height`) und wird beim
   * Laden noch einmal am Element nachgesehen: ein Bild von vor der Zeit, als
   * die Maße mitkamen, hat dort Null stehen, und mit Null stimmt keine der
   * Rechnungen.
   */
  const [natur, setNatur] = useState<Masse | null>(null)

  const flaeche = useRef<HTMLDivElement>(null)
  /** Zwei Finger: der Abstand beim Aufsetzen und der Zoom dazu. */
  const hand = useRef<{ abstand: number; zoom: number } | null>(null)
  /** Der vorige Tipp — für die Unterscheidung von Einzel- und Doppeltipp. */
  const letzterTipp = useRef<{ zeit: number; x: number; y: number } | null>(null)
  /** Die Uhr, die auf den zweiten Tipp wartet. */
  const tippUhr = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** Die Maus zieht am vergrößerten Bild — am Rechner gibt es keinen Finger. */
  const maus = useRef<{ x: number; y: number; versatz: Punkt } | null>(null)
  /** Wann zuletzt ein Finger oben war — gegen den nachgestellten Klick. */
  const zuletztFinger = useRef(0)

  const bild: Masse = natur ?? {
    breite: media[index]?.width ?? 0,
    hoehe: media[index]?.height ?? 0,
  }

  function fenster(): Masse {
    const kasten = flaeche.current?.getBoundingClientRect()
    return { breite: kasten?.width ?? 0, hoehe: kasten?.height ?? 0 }
  }

  /** Ein Bildschirmpunkt, gezählt von der Mitte der Fläche aus. */
  function abMitte(clientX: number, clientY: number): Punkt {
    const kasten = flaeche.current?.getBoundingClientRect()
    if (kasten === undefined) return { x: 0, y: 0 }
    return {
      x: clientX - (kasten.left + kasten.width / 2),
      y: clientY - (kasten.top + kasten.height / 2),
    }
  }

  function setzeZoom(neu: number, aufPunkt: Punkt | null, weich: boolean) {
    const geklemmt = Math.min(Math.max(neu, 1), HAND_DECKEL)
    setSanft(weich)
    setZoom(geklemmt)
    setVersatz((alt) => {
      const gerueckt = aufPunkt === null ? alt : zoomeAuf(aufPunkt, alt, zoom, geklemmt)
      return begrenze(geklemmt === 1 ? { x: 0, y: 0 } : gerueckt, geklemmt, bild, fenster())
    })
  }

  /** Der Doppeltipp: eingepasst ⇄ füllend. */
  function schalteZoom(punkt: Punkt) {
    setzeZoom(zoom > 1.01 ? 1 : fuellFaktor(bild, fenster()), punkt, true)
  }

  /**
   * Ein Tipp — aber welcher?
   *
   * **Der erste Tipp wartet auf den zweiten.** Ohne das Warten flackerte die
   * Leiste bei jedem Doppeltipp einmal weg und wieder her. 250 ms sind der
   * Preis dafür, und sie fallen bei einer Blende, die nur auf- und zugeht,
   * nicht auf.
   */
  function tipp(clientX: number, clientY: number) {
    const jetzt = Date.now()
    const vorher = letzterTipp.current
    const nah =
      vorher !== null &&
      jetzt - vorher.zeit < 300 &&
      Math.abs(clientX - vorher.x) < 30 &&
      Math.abs(clientY - vorher.y) < 30

    if (nah) {
      if (tippUhr.current !== null) clearTimeout(tippUhr.current)
      tippUhr.current = null
      letzterTipp.current = null
      schalteZoom(abMitte(clientX, clientY))
      return
    }

    letzterTipp.current = { zeit: jetzt, x: clientX, y: clientY }
    if (tippUhr.current !== null) clearTimeout(tippUhr.current)
    tippUhr.current = setTimeout(() => {
      tippUhr.current = null
      setNackt((war) => !war)
    }, 250)
  }

  const back = () => setIndex((i) => (i - 1 + media.length) % media.length)
  const forward = () => setIndex((i) => (i + 1) % media.length)

  // Ein neues Bild, eine neue Lage -- „Heruntergeladen." unter dem nächsten
  // Foto wäre eine Auskunft über das vorige. Und der Ausschnitt des vorigen
  // Bildes hat mit dem neuen nichts zu tun: jedes fängt eingepasst an.
  useEffect(() => {
    setMeldung(null)
    setNatur(null)
    setSanft(false)
    setZoom(1)
    setVersatz({ x: 0, y: 0 })
  }, [index])

  /**
   * **Das Telefon wird gedreht — und dann fängt das Bild von vorn an.**
   *
   * Gemessen, bevor es diese Zeilen gab, und beides war unbrauchbar:
   *
   * - Am Telefon (390 × 844 → 844 × 390) blieb der 2,9-fache Zoom stehen. Man
   *   dreht aber gerade *deshalb*, weil man das ganze liegende Bild sehen
   *   will; was dastand, war ein Ausschnitt aus einem Foto, das nun beinahe
   *   von selbst passt.
   * - Am iPad (1180 × 820 → 820 × 1180) blieb ein Zoom von 1,24 aus der
   *   Querlage: das Bild ragte seitlich um 97 Punkte hinaus und ließ oben und
   *   unten je 305 Punkte Grau stehen. Ein Zoom, der weder füllt noch passt.
   *
   * **Zurückgesetzt wird nur beim Kippen, nicht bei jeder Änderung.** Auf iOS
   * meldet ein Fenster auch dann `resize`, wenn nur die Werkzeugleisten des
   * Browsers ein- und ausfahren — wer darauf zurücksetzt, nimmt einem Menschen
   * mitten im Betrachten den Ausschnitt weg. Gekippt ist gekippt: hoch wird
   * quer oder umgekehrt.
   *
   * **Der Ausschnitt wird trotzdem bei jeder Änderung nachgezogen.** Ein
   * geteiltes Fenster am iPad wird schmaler, ohne zu kippen — und ein Versatz,
   * der für das breitere Fenster galt, ließe dort Grau am Rand stehen.
   */
  useEffect(() => {
    let quer = window.innerWidth > window.innerHeight
    function gemessen() {
      const jetzt = window.innerWidth > window.innerHeight
      if (jetzt !== quer) {
        quer = jetzt
        setSanft(false)
        setZoom(1)
        setVersatz({ x: 0, y: 0 })
        return
      }
      setVersatz((alt) => begrenze(alt, zoom, bild, fenster()))
    }
    window.addEventListener('resize', gemessen)
    window.addEventListener('orientationchange', gemessen)
    return () => {
      window.removeEventListener('resize', gemessen)
      window.removeEventListener('orientationchange', gemessen)
    }
    // `zoom` und `bild` stehen im Nachziehen -- ohne sie zöge es gegen einen
    // alten Stand nach.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom, bild.breite, bild.hoehe])

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose()
      if (event.key === 'ArrowLeft') back()
      if (event.key === 'ArrowRight') forward()
    }
    window.addEventListener('keydown', onKey)

    // Der Verlauf darunter soll nicht mitscrollen, während oben ein Bild steht.
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = previous
      if (tippUhr.current !== null) clearTimeout(tippUhr.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [media.length])

  return createPortal(
    <div
      className={nackt ? 'lightbox lightbox--nackt' : 'lightbox'}
      role="dialog"
      aria-modal="true"
      aria-label="Foto in Großansicht"
      onClick={onClose}
      /*
       * **Die Geste endet hier.**
       *
       * Gemeldet aus dem Betrieb: „wenn aus der Highlight-Detailkarte die
       * Fotos geöffnet werden, wird beim Wischen zum nächsten Highlight
       * gewechselt und nicht zum nächsten Foto. Da wird wohl an das falsche
       * Fenster weitergegeben." Genau so war es.
       *
       * **Und der Grund ist eine Eigenheit von `createPortal`:** im DOM hängt
       * dieses Blatt an `document.body`, aber Reacts Ereignisse steigen
       * weiterhin den **React-Baum** hinauf — also dorthin, wo `<Gallery>` im
       * Quelltext steht. Das ist in `HighlightBlatt` mitten im Blatt, und
       * dessen `onTouchEnd` blättert bei einem waagerechten Wisch zum
       * nächsten Punkt. Ein Wisch, zwei Ebenen.
       *
       * **Deshalb steht die Sperre hier und nicht dort.** Die Großansicht
       * liegt obenauf; ihre Gesten sind ihre. So gilt es für jede Ebene
       * darunter, auch für die nächste, die jemand baut — eine Vorsichtsregel
       * in `HighlightBlatt` hätte nur diesen einen Fall gedeckt.
       *
       * **Seit #60 gilt das auch für `onTouchMove`**, und das ist keine
       * Förmlichkeit: das Schieben im vergrößerten Bild ist genau die
       * waagerechte Bewegung, die unten den Punkt weiterblättern würde.
       *
       * **Die Tastatur bleibt davon unberührt** und braucht ihre eigene
       * Sperre (`HighlightBlatt`, `document.querySelector('.lightbox')`): dort
       * hängen beide Zuhörer unmittelbar an `window`, da steigt nichts auf,
       * was man anhalten könnte.
       */
      onTouchStart={(event) => {
        event.stopPropagation()
        if (event.touches.length === 2) {
          const [a, b] = [event.touches[0]!, event.touches[1]!]
          hand.current = {
            abstand: abstand(
              { x: a.clientX, y: a.clientY },
              { x: b.clientX, y: b.clientY },
            ),
            zoom,
          }
          griff.current = null
          return
        }
        const finger = event.touches[0]
        if (finger === undefined) return
        griff.current = {
          x: finger.clientX,
          y: finger.clientY,
          versatz,
          zeit: Date.now(),
        }
      }}
      onTouchMove={(event) => {
        event.stopPropagation()

        // Zwei Finger ziehen stufenlos.
        if (hand.current !== null && event.touches.length === 2) {
          const [a, b] = [event.touches[0]!, event.touches[1]!]
          const jetzt = abstand({ x: a.clientX, y: a.clientY }, { x: b.clientX, y: b.clientY })
          if (hand.current.abstand > 0) {
            const mitteX = (a.clientX + b.clientX) / 2
            const mitteY = (a.clientY + b.clientY) / 2
            setzeZoom(
              (hand.current.zoom * jetzt) / hand.current.abstand,
              abMitte(mitteX, mitteY),
              false,
            )
          }
          return
        }

        // Ein Finger schiebt -- aber nur, wenn es etwas zu schieben gibt.
        const zettel = griff.current
        if (zettel === null || zoom <= 1) return
        const finger = event.touches[0]
        if (finger === undefined) return
        setSanft(false)
        setVersatz(
          begrenze(
            {
              x: zettel.versatz.x + (finger.clientX - zettel.x),
              y: zettel.versatz.y + (finger.clientY - zettel.y),
            },
            zoom,
            bild,
            fenster(),
          ),
        )
      }}
      onTouchEnd={(event) => {
        event.stopPropagation()
        zuletztFinger.current = Date.now()

        if (hand.current !== null) {
          hand.current = null
          // Fast wieder eingepasst: dann ganz. Sonst bliebe ein Ausschnitt bei
          // 1,02 stehen, den niemand gemeint hat.
          if (zoom < 1.05) setzeZoom(1, null, true)
          return
        }

        const zettel = griff.current
        griff.current = null
        if (zettel === null) return

        const finger = event.changedTouches[0]
        const dx = (finger?.clientX ?? zettel.x) - zettel.x
        const dy = (finger?.clientY ?? zettel.y) - zettel.y

        // **Geblättert wird nur eingepasst.** Vergrößert ist dieselbe Bewegung
        // das Schieben des Ausschnitts, und beides zugleich ginge nicht.
        if (zoom <= 1) {
          // Unter 50 Pixeln war es ein Tippen und kein Wischen.
          if (dx > 50) return back()
          if (dx < -50) return forward()
        }

        // Ein Tippen: unter zehn Punkten in beiden Richtungen.
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) {
          tipp(zettel.x, zettel.y)
        }
      }}
    >
      {/* **Eine eigene Zeile und kein Schweben.** Über einem hohen Foto —
          einem Bildschirmfoto etwa — lag der Knopf vorher mitten im Bild:
          weiß auf hell und damit weder zu sehen noch zu treffen.

          Seit #60 liegt er wieder über dem Bild — aber mit einem dunklen
          Verlauf darunter, der genau dieses Problem löst, und er tritt bei
          einem Tipp ganz beiseite. */}
      <div className="lightbox__kopf">
        <button type="button" className="lightbox__close" onClick={onClose} aria-label="Schließen">
          <XIcon size={22} />
        </button>
      </div>

      {media.length > 1 && (
        <>
          <button
            type="button"
            className="lightbox__step lightbox__step--back"
            onClick={(event) => {
              event.stopPropagation()
              back()
            }}
            aria-label="Vorheriges Foto"
          >
            <ChevronLeftIcon size={26} />
          </button>
          <button
            type="button"
            className="lightbox__step lightbox__step--forward"
            onClick={(event) => {
              event.stopPropagation()
              forward()
            }}
            aria-label="Nächstes Foto"
          >
            <ChevronRightIcon size={26} />
          </button>
        </>
      )}

      {/* **Der Kasten dazwischen ist nicht schmückend.** Ein Bild als
          unmittelbares Kind der Rasterzelle rechnet seinen Deckel
          (`max-height: 100%`) gegen die Zelle -- und WebKit tut das nicht.
          Über einem Bildschirmfoto lag es deshalb über Kreuz und Leiste. Der
          Kasten hat eine gewöhnliche Höhe und schneidet notfalls ab.

          Seit #60 ist er zugleich der Bildschirm: er liegt über der ganzen
          Fläche, und Kopf und Leiste liegen über ihm. */}
      <div
        className="lightbox__flaeche"
        ref={flaeche}
        /* Am Rechner gibt es keinen zweiten Finger — dort zieht das Rad. */
        onWheel={(event) => {
          event.stopPropagation()
          setzeZoom(
            zoom * (1 - event.deltaY / 500),
            abMitte(event.clientX, event.clientY),
            false,
          )
        }}
        onPointerDown={(event) => {
          if (event.pointerType === 'touch' || zoom <= 1) return
          maus.current = { x: event.clientX, y: event.clientY, versatz }
        }}
        onPointerMove={(event) => {
          const zettel = maus.current
          if (zettel === null) return
          setSanft(false)
          setVersatz(
            begrenze(
              {
                x: zettel.versatz.x + (event.clientX - zettel.x),
                y: zettel.versatz.y + (event.clientY - zettel.y),
              },
              zoom,
              bild,
              fenster(),
            ),
          )
        }}
        onPointerUp={() => {
          maus.current = null
        }}
        onPointerLeave={() => {
          maus.current = null
        }}
      >
        <img
          className="lightbox__image"
          src={media[index]?.url}
          alt=""
          style={{
            transform: `translate(${versatz.x}px, ${versatz.y}px) scale(${zoom})`,
            transition: sanft ? 'transform 180ms ease-out' : 'none',
            cursor: zoom > 1 ? 'grab' : 'default',
          }}
          onLoad={(event) => {
            const el = event.currentTarget
            if (el.naturalWidth > 0) {
              setNatur({ breite: el.naturalWidth, hoehe: el.naturalHeight })
            }
          }}
          /* Der Klick gehört dem Bild und schließt nicht -- daneben schon. */
          onClick={(event) => {
            event.stopPropagation()
            /*
             * **Nur die Maus, nicht der Finger.**
             *
             * Ein Berührbildschirm schickt nach `touchend` noch einen
             * nachgestellten Klick hinterher — derselbe Tipp käme hier ein
             * zweites Mal an und hätte den Doppeltipp ausgelöst, den niemand
             * gemacht hat. Der Ereignistyp hilft nicht weiter: React nennt
             * das native Ereignis `MouseEvent`, und `detail` steht bei beiden
             * auf eins. Also merkt sich der Finger, wann er zuletzt oben war.
             */
            if (event.detail === 0) return
            if (Date.now() - zuletztFinger.current < 700) return
            tipp(event.clientX, event.clientY)
          }}
        />
      </div>

      <div className="lightbox__bar" onClick={(event) => event.stopPropagation()}>
        {media[index] && (canReact || reactionsFor(media[index].id).length > 0) && (
          <Reactions
            compact
            reactions={reactionsFor(media[index].id)}
            onPick={(emoji) => canReact && onReact(emoji, media[index]!.id)}
          />
        )}

        <div className="lightbox__aktionen">
          {/*
            **Sichern gehört der Anwendung und nicht der Geste** (#58). Auf iOS
            legt ein langes Drücken das Bild in „Fotos", auf Android passiert
            dabei nichts — und wo Chrome dort doch ein Menü zeigt, landet die
            Datei in *Downloads* und nicht in der Galerie. Der Knopf tut auf
            beiden Geräten dasselbe: er reicht das Bild ans System weiter.
          */}
          {media[index] && (
            <button
              type="button"
              className="lightbox__antwort"
              disabled={laeuft}
              onClick={async () => {
                const foto = media[index]
                if (foto === undefined) return
                setLaeuft(true)
                setMeldung(null)
                try {
                  const weg = await sichereFoto(foto)
                  /*
                   * **Nach dem Teilen-Blatt bleibt es still** — das Gerät hat
                   * schon geantwortet, ein zweiter Satz wäre Geplapper.
                   *
                   * **Nach einem Download steht dagegen die eigentliche Frage
                   * im Raum: wo ist es hin?** Auf Android landet es im Ordner
                   * *Downloads*, und den zeigt jede Galerie-App als Album —
                   * das weiß nur, wer es gesagt bekommt. „Heruntergeladen."
                   * stand hier vorher und beantwortete genau das nicht.
                   */
                  if (weg === 'geladen') {
                    setMeldung('Im Ordner „Downloads" gespeichert — dort findet ihn auch die Galerie.')
                  }
                } catch {
                  setMeldung('Das Foto ließ sich nicht holen. Ohne Verbindung geht es nicht.')
                } finally {
                  setLaeuft(false)
                }
              }}
            >
              <SichernIcon size={16} />
              {laeuft ? 'Einen Moment …' : 'Foto sichern'}
            </button>
          )}

          {onReply && media[index] && (
            <button
              type="button"
              className="lightbox__antwort"
              onClick={() => {
                onReply(media[index]!.id)
                // Zumachen gehört dazu: geantwortet wird unten im Schreibfeld,
                // und das liegt hinter diesem Bild.
                onClose()
              }}
            >
              <ReplyIcon size={16} />
              Auf dieses Foto antworten
            </button>
          )}
        </div>

        {meldung !== null && (
          <p className="lightbox__meldung" role="alert">
            {meldung}
          </p>
        )}
        {media.length > 1 && (
          <span className="lightbox__count">
            {index + 1} von {media.length}
          </span>
        )}
      </div>
    </div>,
    document.body,
  )
}
